import React, { useState } from 'react';
import AppHeader from '../../common/app-header/AppHeader';
import { useLibraryItemDetails } from './hooks/useLibraryItemDetails';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Input,
  Modal,
  Tooltip,
  Typography,
} from '@mui/material';
import styles from './LibraryItemDetails.module.scss';
import { ROUTES } from '../../common/constants/routes';
import { Link, useLocation, useParams } from 'react-router-dom';
//import BrushIcon from '../../../assets/svg/brush.svg?react';
import { ProgressOverlay } from '../../common/progress-overlay/ProgressOverlay';
import ContractGenerator from '../../common/contract-generator/ContractGenerator';
import { useTranslation } from 'react-i18next';
import FileTypeDownloadModal from '../../common/file-type-download-modal/FileTypeDownloadModal';
import { useFileTypeDownload } from '../../common/file-type-download-modal/useFileTypeDownload';
import FileSaveIcon from '../../../assets/svg/file_save.svg?react';
import { LibraryItem } from '../library/components/library-list/types';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { selectAllDocuments } from '../../slices/documents/documentsSlice';
import { useLibraryFunctions } from '../library/hooks/useLibraryFunctions';
import { useDeleteLibraryItemMessageModal } from '../library/hooks/useDeleteMessageModal';
import MessageModal from '../../common/message-modal/MessageModal';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DisclaimerText from '../../common/disclaimer-text/DisclaimerText';
import { acceptDisclaimer, selectDisclaimerChecked } from '../../slices/disclaimer/disclaimerSlice';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { selectAllCompanies } from '../../slices/companies/companiesSlice';
import ContentCopyIcon from '../../../assets/svg/content_copy.svg?react';
import { selectUser } from '../../slices/user/userSlice';
import { ErrorObject, Helpers } from '../../common/helpers/helpers';
import { useErrorMessage } from '../../common/hooks/useErrorMessage';
import { toast } from 'react-toastify';
import { showMessage } from '../../slices/show-plan-upgrading-message/showPlanUpgradingMessage';
import LibraryItemDetailsBlock from './components/library-item-details/LibraryItemDetailsBlock';
import { Editor } from '@tinymce/tinymce-react';
import BrushIcon from '../../../assets/svg/brush.svg?react';

const CopyModal: React.FC<{ item: LibraryItem; copyModalOpen: any; onClose: () => void }> = ({
  item,
  copyModalOpen,
  onClose,
}) => {
  const { handleLibraryItemCopy, handleTemplateCopy } = useLibraryFunctions();
  const currentUser = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { getErrorMessage } = useErrorMessage();
  // Stamped once when the modal mounts, so the clock is not read during render.
  const [defaultTemplateName] = useState(() => {
    const stampedAt = new Date();
    const date = stampedAt.toLocaleDateString();
    const time = stampedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const baseName = item.name ? item.name : t('templatePage.headerDefault');
    return `${baseName} - ${date} ${time}`;
  });
  const handleCopying = (newname: string) => {
    const data = {
      name: newname,
      category: item.category,
      dt_workflows: item.dl_workflows,
      dt_types: item.dl_types,
      user: currentUser.id,
    };

    try {
      handleLibraryItemCopy(data, item.id); // Wait for the copy operation to complete
      onClose(); // Close the modal after the operation finishes
    } catch (error) {
      const err = error as ErrorObject & {
        response?: { data?: { errors?: string[] } };
      };
      if (
        (err.response?.data?.errors?.length ?? 0) > 0 &&
        err.response?.data?.errors?.[0] === t('messages.exceededPlanLimits')
      ) {
        dispatch(showMessage(t('messages.monthLimitReached')));
      } else {
        toast.error(getErrorMessage(err));
      }
    }
  };
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const newTemplateName = formData.get('newTemplateName') as string;
    if ('region' in item) {
      handleCopying(newTemplateName);
    } else {
      const updatedItem = { ...item, name: newTemplateName };
      try {
        handleTemplateCopy(updatedItem, currentUser.id);
        onClose();
      } catch (error) {
        const err = error as ErrorObject & {
          response?: { data?: { errors?: string[] } };
        };
        if (
          (err.response?.data?.errors?.length ?? 0) > 0 &&
          err.response?.data?.errors?.[0] === t('messages.exceededPlanLimits')
        ) {
          dispatch(showMessage(t('messages.monthLimitReached')));
        } else {
          toast.error(getErrorMessage(err));
        }
      }
    }
  };
  return (
    <Modal open={copyModalOpen?.id === item.id} className={styles.copymodal} onClose={onClose}>
      <Grid component='div' container sx={{ boxShadow: 'none', border: 'none', outline: 'none' }}>
        <Grid component='div' size={{ xs: 12, sm: 11, md: 11, lg: 11 }}>
          <Card>
            <CardContent sx={{ paddingBottom: '16px !important' }}>
              <Typography variant={'h6'} gutterBottom>
                {t('headers.copyToMyTemplates')}
              </Typography>

              <Typography>Please change the template name if necessary.</Typography>
              <form onSubmit={handleSubmit}>
                <Box sx={{ maxHeight: '500px', overflow: 'auto' }}>
                  <Input
                    name='newTemplateName'
                    placeholder={item.name ? item.name : t('templatePage.headerDefault')}
                    defaultValue={defaultTemplateName}
                    className={styles.input}
                    fullWidth
                    required
                  />
                </Box>

                <Grid
                  component='div'
                  container
                  style={{
                    justifyContent: 'flex-end',
                    gap: '8px',
                    flexWrap: 'wrap',
                    marginTop: '16px',
                  }}
                >
                  <Button
                    id='libraryItemDetailsGoBackBtn'
                    variant='contained'
                    onClick={onClose}
                    className={'btn-default btn-action-basic btn-cancel'}
                  >
                    Go back
                  </Button>

                  <Button
                    id='libraryItemDetailsConfirmBtn'
                    variant='contained'
                    type='submit'
                    className='btn-default btn-action-basic'
                  >
                    Confirm
                  </Button>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Modal>
  );
};

const LibraryItemDetails = () => {
  const [copyModalOpen, setCopyModalOpen] = useState<LibraryItem | null>(null);
  const [pdfSaveDialogOpen, setPdfSaveDialogOpen] = useState(false);
  const { name } = useParams();
  const documentUploads = useAppSelector(selectAllDocuments);
  const {
    dlHTML,
    setDlHTML,
    templateTitle,
    editMode,
    setEditMode,
    loadingHTML,
    contractCreationEnabled,
    setContractCreationEnabled,
    templateCategory,
    templateItem,
    handleSave,
    loading: savingTemplate,
    documentTypeLabel,
    setDocumentTypeLabel,
    setDocumentWorkflowLabel,
    documentWorkflowLabel,
    propertiesEditMode,
    setPropertiesEditMode,
    category,
    setCategory,
    categorySelectValues,
    getTypeLabels,
    setTemplateName,
    templateName,
    customLabel,
    setCustomLabel,
    workflowsArray,
    updateLibraryItem,
    setBlockInfoInitial,
  } = useLibraryItemDetails();
  const {
    setModalOpen: setFileTypeModalOpen,
    modalOpen: fileTypeModalOpen,
    handleCloseModal: handleCloseFileTypeModal,
    libraryItem: libraryItemForDownloading,
    setLibraryItem: setLibraryItemForDownloading,
    setFileName: setFileNameForDownloading,
    fileName: fileNameForDownloading,
    fileType: fileTypeForDownloading,
    setFileType: setFileTypeForDownloading,
    handleTemplateDownload,
  } = useFileTypeDownload();
  const { handleS3DownloadLink, disclaimerMessage, setDisclaimerMessage } = useLibraryFunctions();
  const {
    modalOpen: deleteLibraryItemModalOpen,
    setModalOpen: setDeleteLibraryItemModalOpen,
    handleDeleteDocumentTemplate,
    libraryItemToDelete,
    setLibraryItemToDelete,
    pendingStatus: deletingLibraryItemPending,
  } = useDeleteLibraryItemMessageModal();
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectAllCompanies)[0];
  const disclaimerAccepted = useAppSelector(selectDisclaimerChecked);
  const disclaimerStatus = useAppSelector((state) => state.disclaimerInfo.status);
  const freePlan =
    (!company?.stripe_product_id && !company?.plan_settings.stripe_product_id) ||
    company?.stripe_product_name?.includes('eSign');
  const actionAvailableFree = templateItem && 'is_free' in templateItem && templateItem?.is_free;
  const templateLabels =
    templateItem && 'region' in templateItem
      ? {
          categoryLabel: templateItem.category,
          typeLabel: templateItem.dl_types?.[0],
          workflowLabel: templateItem.dl_workflows?.[0],
        }
      : null;

  // templateItem is nullable and its `name` is optional on DocumentTemplateModel,
  // while LibraryItem requires a non-optional `name`.
  const templateLibraryItem: LibraryItem | null = templateItem
    ? ({ ...templateItem, name: templateItem.name ?? '' } as LibraryItem)
    : null;

  const handleGenerateContract = () => {
    if (!templateItem) {
      return;
    }
    const actionAvailableFree = 'is_free' in templateItem && templateItem.is_free;
    if ((freePlan && actionAvailableFree) || !freePlan || !('region' in templateItem)) {
      if (disclaimerAccepted) {
        setContractCreationEnabled(true);
      } else {
        setDisclaimerMessage(true);
      }
    } else if (freePlan && !actionAvailableFree) {
      dispatch(showMessage(t('messages.considerStartingPlanLibrary')));
    }
  };
  const handleDownloading = (item: LibraryItem) => {
    if (!item.download_link) {
      setFileNameForDownloading(
        item.name ??
          documentUploads
            ?.find((el) => el.id === item.document_upload)
            ?.name?.replace('.pdf', '')
            .replace('.docx', '')
            .replace('.doc', '') ??
          ''
      );
      setLibraryItemForDownloading(item);
      setFileTypeModalOpen(true);
    } else {
      handleS3DownloadLink(item.id, 'name' in item ? item.name : '');
    }
  };
  return (
    <>
      <AppHeader
        title={
          location.pathname.includes(ROUTES.DOCUMENT_LIBRARY)
            ? t('headers.documentTemplates')
            : t('headers.workspace')
        }
      />
      <Box sx={{ marginTop: '16px' }}>
        <Grid component='div' container className={styles.header}>
          <Grid
            component='div'
            sx={{ display: { md: 'flex' }, justifyContent: { md: 'space-between' } }}
            size={{ xs: 12 }}
          >
            <Box className={styles.breadcrumbs}>
              <Link
                id='libraryItemDetailsBreadcrumbLibraryLink'
                to={
                  ROUTES.DOCUMENT_LIBRARY +
                  (name === 'uk' || name === 'au' || name === 'global'
                    ? `?region=${name}`
                    : '?libraryType=own')
                }
                className={styles.documentViewText}
              >
                {name === 'uk' || name === 'au' || name === 'global'
                  ? t('buttons.businessKitzLibrary')
                  : t('buttons.ownLibrary')}
              </Link>
              <Typography variant='body2' className={styles.separator}>
                ›
              </Typography>
              {templateItem?.category && (
                <>
                  <Link
                    id='libraryItemDetailsBreadcrumbCategoryLink'
                    to={`${ROUTES.DOCUMENT_LIBRARY}?category=${templateItem?.category}${
                      name === 'uk' || name === 'au' || name === 'global'
                        ? `&region=${name}`
                        : '&libraryType=own'
                    }`}
                    className={styles.documentViewText}
                  >
                    {templateItem?.category
                      ? Helpers.capitalizeFirstLetter(templateItem.category)
                      : ''}
                  </Link>
                  <Typography variant='body2' className={styles.separator}>
                    ›
                  </Typography>
                  {templateItem?.dl_workflows && templateItem?.dl_workflows.length > 0 && (
                    <>
                      <Link
                        id='libraryItemDetailsBreadcrumbWorkflowLink'
                        to={`${ROUTES.DOCUMENT_LIBRARY}?category=${templateItem?.category}&workflow=${encodeURIComponent(
                          templateItem?.dl_workflows[0] ?? ''
                        )}${name === 'uk' || name === 'au' || name === 'global' ? `&region=${name}` : ''}`}
                        className={styles.documentViewText}
                      >
                        {templateItem?.dl_workflows.length > 0
                          ? Helpers.capitalizeFirstLetter(templateItem?.dl_workflows[0])
                          : ''}
                      </Link>
                      <Typography variant='body2' className={styles.separator}>
                        ›
                      </Typography>
                    </>
                  )}
                </>
              )}
              <Typography variant='body2' className={styles.documentViewText}>
                {templateTitle}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                rowGap: '8px',
                marginTop: { xs: '16px', md: 0 },
              }}
            >
              <Button
                id='libraryItemDetailsGenerateContractBtn'
                style={{ marginRight: '8px', flexShrink: 0 }}
                type='button'
                className='btn-default btn-small btn-action-basic'
                role='button'
                variant='contained'
                onClick={() => {
                  handleGenerateContract();
                }}
                disabled={contractCreationEnabled}
              >
                {t('buttons.generateContract')}
              </Button>
              {templateCategory === 'company-templates' ? (
                editMode ? (
                  <>
                    <Button
                      id='libraryItemDetailsSaveBtn'
                      style={{ marginRight: '8px' }}
                      type='button'
                      className='btn-default btn-small btn-action-basic'
                      role='button'
                      variant='contained'
                      disabled={savingTemplate}
                      onClick={() => {
                        handleSave();
                      }}
                    >
                      {t('buttons.save')}
                    </Button>
                    <Button
                      id='libraryItemDetailsCancelBtn'
                      style={{ marginRight: '8px' }}
                      type='button'
                      className='btn-default btn-small btn-action-basic btn-transparent-initial btn-cancel'
                      role='button'
                      variant='outlined'
                      onClick={() => {
                        setEditMode(false);
                      }}
                    >
                      {t('buttons.cancel')}
                    </Button>
                  </>
                ) : (
                  <Tooltip title={t('buttons.edit')} placement='top' arrow>
                    <IconButton
                      id='libraryItemDetailsEditIconBtn'
                      size='small'
                      className={styles.editButton}
                      onClick={() => setEditMode(true)}
                    >
                      <BrushIcon id='libraryItemDetailsEditIconBtnBrushIcon' />
                    </IconButton>
                  </Tooltip>
                )
              ) : null}
              <Tooltip title={t('buttons.download')} placement='top' arrow>
                <IconButton
                  id='libraryItemDetailsDownloadIconBtn'
                  size='small'
                  className={styles.actionButton}
                  onClick={() => {
                    if (templateLibraryItem) {
                      handleDownloading(templateLibraryItem);
                    }
                  }}
                >
                  <FileSaveIcon id='libraryItemDetailsDownloadIconBtnFileSaveIcon' />
                </IconButton>
              </Tooltip>
              {!templateItem?.download_link && (
                <Tooltip title={t('buttons.copyTemplate')} placement='top' arrow>
                  <IconButton
                    id='libraryItemDetailsCopyDocumentIconBtn'
                    size='small'
                    onClick={() => {
                      if (freePlan || !templateItem?.region) {
                        if (actionAvailableFree || !templateItem?.region) {
                          setCopyModalOpen(templateLibraryItem);
                        } else {
                          dispatch(showMessage(t('messages.considerStartingPlanLibrary')));
                        }
                      } else {
                        setCopyModalOpen(templateLibraryItem);
                      }
                    }}
                    className={styles.action_button}
                  >
                    <ContentCopyIcon id='libraryItemDetailsCopyDocumentIconBtnContentCopyIcon' />
                  </IconButton>
                </Tooltip>
              )}
              {!templateItem?.region && (
                <Tooltip title={t('messages.delete')} placement='top' arrow>
                  <IconButton
                    id='libraryItemDetailsDeleteIconBtn'
                    size='small'
                    aria-label='delete'
                    onClick={() => {
                      setLibraryItemToDelete(templateLibraryItem);
                      setDeleteLibraryItemModalOpen(true);
                    }}
                  >
                    <DeleteOutlineOutlinedIcon
                      id='libraryItemDetailsDeleteIconBtnDeleteOutlineIcon'
                      style={{ marginRight: 0, fill: '#152a58', width: '20px', height: '20px' }}
                    />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Grid>
        </Grid>
        <Grid
          component='div'
          container
          spacing={2}
          className='inputs-editable'
          style={{ justifyContent: 'space-between' }}
        >
          <Grid
            component='div'
            size={{ xs: 12 }}
            className={styles.gridItem}
            sx={{ position: 'relative', paddingRight: '16px', paddingBottom: '0' }}
          >
            <Box
              style={{
                display: 'flex',
                paddingBottom: '16px',
                paddingRight: '8px',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                rowGap: '8px',
              }}
            >
              <Box>
                <Grid
                  component='div'
                  container
                  sx={{
                    marginBottom: 0.5,
                    paddingTop: 0,
                    paddingBottom: 0,
                    paddingLeft: 0,
                    paddingRight: 0,
                  }}
                >
                  <Typography variant='h2' className={styles.title}>
                    {templateTitle}
                  </Typography>
                </Grid>
                {templateLabels && (
                  <Grid component='div' container className={styles.labels_row}>
                    {templateLabels && templateLabels.categoryLabel
                      ? templateLabels.categoryLabel
                      : null}
                    {templateLabels && templateLabels.workflowLabel
                      ? ' / ' + templateLabels.workflowLabel
                      : null}
                    {templateLabels && templateLabels.typeLabel
                      ? ' / ' + templateLabels.typeLabel
                      : null}
                  </Grid>
                )}
              </Box>
            </Box>
            <Box
              className={styles.tabContent}
              sx={{
                height: { xs: 'auto', md: `calc(100vh - 183px)` },
                overflow: loadingHTML ? 'hidden' : 'auto',
              }}
            >
              <Box className={styles.mainContent}>
                {contractCreationEnabled && (
                  <Box sx={{ width: { xs: '100%', md: '35%' }, flexShrink: 0 }}>
                    <ContractGenerator
                      templateTitle={templateTitle}
                      libraryItem={templateLibraryItem ?? undefined}
                      templateId={templateItem?.id ?? 0}
                      setContractCreationEnabled={setContractCreationEnabled}
                    />
                  </Box>
                )}
                {!contractCreationEnabled && templateItem && 'document_upload' in templateItem && (
                  <Box sx={{ width: { xs: '100%', md: '35%' }, flexShrink: 0 }}>
                    <LibraryItemDetailsBlock
                      resetBlockInfo={() => {
                        setBlockInfoInitial();
                      }}
                      categoryArray={categorySelectValues}
                      workflowsArray={workflowsArray()}
                      typeLabels={getTypeLabels()}
                      propertiesEditMode={propertiesEditMode}
                      setPropertiesEditMode={setPropertiesEditMode}
                      documentTypeLabel={documentTypeLabel}
                      setDocumentTypeLabel={setDocumentTypeLabel}
                      documentWorkflowLabel={documentWorkflowLabel}
                      setDocumentWorkflowLabel={setDocumentWorkflowLabel}
                      templateName={templateName}
                      setTemplateName={setTemplateName}
                      category={category}
                      setCategory={setCategory}
                      updateLibraryItem={() => updateLibraryItem()}
                      customLabel={customLabel}
                      setCustomLabel={setCustomLabel}
                    />
                  </Box>
                )}
                <div
                  className={`${styles.main_container} ${
                    contractCreationEnabled || (templateItem && 'document_upload' in templateItem)
                      ? styles.w_55
                      : ''
                  }`}
                >
                  <div
                    id='editor'
                    className='main-editor-container template-editor'
                    style={{ height: `calc(100vh - 183px)` }}
                  >
                    {templateItem && (
                      <Editor
                        tinymceScriptSrc='/tinymce/tinymce.min.js'
                        licenseKey='gpl'
                        // A company template (one of the account's own) is edited in place once
                        // the header Edit is pressed; Business Kitz templates stay read-only.
                        disabled={templateCategory === 'company-templates' ? !editMode : true}
                        init={{
                          content_style:
                            '#tinymce {max-width: 210mm; margin-left: auto; margin-right: auto; border: none; box-shadow: none; img {display: none !important;} .tox-promotion {display: none !important;}',
                          menubar:
                            templateItem && 'document_upload' in templateItem
                              ? 'edit insert view format table tools'
                              : false,
                          extended_valid_elements: 'style',
                          valid_children: '+body[style]',
                          plugins:
                            templateItem && 'document_upload' in templateItem
                              ? [
                                  // Core editing features
                                  'anchor',
                                  'autolink',
                                  'charmap',
                                  'codesample',
                                  'emoticons',
                                  'link',
                                  'lists',
                                  //'media',
                                  'searchreplace',
                                  'table',
                                  'visualblocks',
                                  'wordcount',
                                  // Your account includes a free trial of TinyMCE premium features
                                  // Try the most popular premium features until Jan 31, 2026:
                                  /*'checklist',
                                'mediaembed',
                                'casechange',
                                'formatpainter',
                                'pageembed',
                                'a11ychecker',
                                'tinymcespellchecker',
                                'permanentpen',
                                'powerpaste',
                                'advtable',
                                'advcode',
                                'advtemplate',
                                'ai',
                                'uploadcare',
                                //'mentions',
                                //'tinycomments',
                                'tableofcontents',
                                //'footnotes',
                                //'mergetags',
                                'autocorrect',
                                'typography',
                                'inlinecss',
                                'markdown',
                                //'importword',
                                //'exportword',
                                //'exportpdf',*/
                                ]
                              : [],
                          toolbar: false,
                          /*'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | link table | spellcheckdialog a11ycheck typography uploadcare | align lineheight | checklist numlist bullist indent outdent | emoticons charmap | removeformat'*/
                          ai_request: (request: any, respondWith: any) =>
                            respondWith.string(() =>
                              Promise.reject('See docs to implement AI Assistant')
                            ),
                          uploadcare_public_key: 'b97265d9de273c24383a',
                          // XSS prevention: strip <script> tags and on* event attributes from pasted/loaded content
                          invalid_elements: 'script,object,embed,applet,iframe,form,input,button',
                          cleanup_on_startup: true,
                          convert_unsafe_embeds: true,
                          sandbox_iframes: true,
                          link_assume_external_targets: false,
                          allow_script_urls: false,
                        }}
                        value={dlHTML}
                        onEditorChange={(value) => {
                          if (value) {
                            setDlHTML(value);
                          } else {
                            setDlHTML('');
                          }
                        }}
                      />
                    )}
                  </div>
                </div>
              </Box>
            </Box>
            {(loadingHTML || disclaimerStatus === 'loading' || savingTemplate) && (
              <ProgressOverlay position='absolute' />
            )}
          </Grid>
        </Grid>
      </Box>
      {fileTypeModalOpen && libraryItemForDownloading && (
        <FileTypeDownloadModal
          onClose={() => {
            handleCloseFileTypeModal();
          }}
          open={fileTypeModalOpen}
          fileName={fileNameForDownloading}
          fileType={fileTypeForDownloading}
          handleFileTypeChange={(value) => setFileTypeForDownloading(value as 'pdf' | 'doc')}
          handleFileNameChange={setFileNameForDownloading}
          libraryItem={libraryItemForDownloading}
          handleDownloadLibraryHTML={() => {
            if (fileTypeForDownloading === 'pdf') {
              setPdfSaveDialogOpen(true);
              setFileTypeModalOpen(false);
            } else {
              handleTemplateDownload(
                libraryItemForDownloading.id,
                fileTypeForDownloading,
                fileNameForDownloading,
                'document_upload' in libraryItemForDownloading
              );
            }
          }}
        />
      )}
      {pdfSaveDialogOpen && (
        <MessageModal
          open={pdfSaveDialogOpen}
          modalMessage={t('messages.chooseSaveAsPDF')}
          actionButtonText={t('buttons.ok')}
          actionButtonFn={() => {
            Helpers.convertHtmlToPdfAndDownload(dlHTML, fileNameForDownloading);
            setPdfSaveDialogOpen(false);
          }}
          goBackButtonText={t('buttons.goBack')}
          onClose={() => {
            setPdfSaveDialogOpen(false);
          }}
        />
      )}
      {deleteLibraryItemModalOpen && libraryItemToDelete && (
        <MessageModal
          open={deleteLibraryItemModalOpen}
          modalTitle={t('dialogTitles.deleteTemplate')}
          modalMessage={t('messages.deleteTemplate')}
          actionButtonText={t('buttons.goBack')}
          actionButtonFn={() => {
            setLibraryItemToDelete(null);
            setDeleteLibraryItemModalOpen(false);
          }}
          goBackButtonText={t('buttons.delete')}
          onDismiss={() => {
            setLibraryItemToDelete(null);
            setDeleteLibraryItemModalOpen(false);
          }}
          onClose={() => {
            handleDeleteDocumentTemplate();
          }}
        />
      )}
      {disclaimerMessage && (
        <MessageModal
          open={disclaimerMessage}
          modalTitle={t('headers.documentTerms')}
          modalContent={<DisclaimerText />}
          actionButtonText={t('buttons.accept')}
          headerVariant={'h5'}
          actionButtonFn={() => {
            dispatch(acceptDisclaimer())
              .unwrap()
              .then(() => {
                setDisclaimerMessage(false);
                setContractCreationEnabled(true);
                //setGeneratorOpened(true);
              });
          }}
          goBackButtonText={t('buttons.goBack')}
          onClose={() => {
            setDisclaimerMessage(false);
          }}
        />
      )}

      {copyModalOpen && templateLibraryItem && (
        <CopyModal
          item={templateLibraryItem}
          copyModalOpen={copyModalOpen}
          onClose={() => {
            setCopyModalOpen(null);
          }}
        />
      )}
      {(deletingLibraryItemPending || disclaimerStatus === 'loadingAccepting') && (
        <ProgressOverlay />
      )}
    </>
  );
};

export default LibraryItemDetails;
